/**
 * Story panel (P707 campaign + plan-9.5 celebrated moments).
 *
 * Moments are the plan-9.5 mechanics from Matthew's master plan; labels come
 * from the sim projection.
 *
 * Campaign order: Matthew approved the existing three-reserve chain as
 * offered - Raven Creek / First Ranch -> Cedar Breaks / Oryx Warden ->
 * Salt Flat Country / Big Country (his "A for both" answer, WhatsApp
 * 2026-10-23 17:47, to the order question's option A). Only the existing
 * scenario/region names are shown: no intro or quest text exists in the
 * approved canon, so none is rendered. Begin is a two-tap confirm because
 * story.begin starts a fresh ranch in place of the current one.
 */
import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney } from '../bridge';
import { storyOf, type UiJournalRequirement, type UiReserveState } from '../story-projection';

const STATE_LABEL: Record<UiReserveState, string> = {
  locked: 'Locked',
  available: 'Available',
  active: 'In progress',
  won: 'Won',
};

function RequirementRow({row}: {row: UiJournalRequirement}) {
  return <div style="margin-top:6px;padding-left:8px;border-left:2px solid var(--ui-ink-faint)"><div class="row-label" style="white-space:normal">{row.met ? 'Met now' : 'Not met'}: {row.label}{row.current !== undefined ? ` - current ${row.currentText??row.current}` : ''}</div>{row.children?.map((child, i) => <RequirementRow key={i} row={child} />)}</div>;
}

export function StoryPanel() {
  const { sim, bridge } = useStore();
  const [pendingBegin, setPendingBegin] = useState<string | null>(null);
  const story = storyOf(sim);
  if (story === null) {
    return (
      <div style="padding:6px 18px 16px">
        <div class="eyebrow eyebrow-rule">Story</div>
        <p style="font-size:13px;color:var(--ui-ink-soft)">Story shows up once the story projection is wired.</p>
      </div>
    );
  }
  const { campaign, moments } = story;
  const wonCount = campaign.reserves.filter((r) => r.state === 'won').length;
  return (
    <div style="padding:6px 18px 16px">
      <div class="eyebrow eyebrow-rule">Story</div>

      {story.journal && <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
        <div class="row-value">Current reserve: {story.journal.name}</div>
        <div class="row-label" style="white-space:normal">{story.journal.status === 'complete' ? 'Won - the next reserve is listed below.' : story.journal.status === 'failed' ? 'Failed - this outcome is final for this run.' : 'In progress - outcomes are checked daily.'}{story.journal.status !== 'active' ? '' : story.journal.daysLeft !== null ? ` ${story.journal.daysLeft} days remaining.` : ' No time limit.'}</div>
        <div class="row-label" style="white-space:normal;margin-top:6px">Current facts update live. Meeting a goal now is not a win until the daily check. Failure and the time limit are checked before the goal.</div>
        {story.journal.goal && <><div class="eyebrow" style="margin-top:8px">Objectives</div><RequirementRow row={story.journal.goal}/></>}
        {story.journal.fail && <><div class="eyebrow" style="margin-top:8px">Failure condition</div><RequirementRow row={story.journal.fail}/></>}
      </div>}

      <div class="eyebrow" style="margin-top:10px">Active objectives</div>
      <div class="row-label" style="white-space:normal;margin-top:6px">Existing daily and weekly challenges. Outcomes are checked daily, including the final expiry boundary. Current facts can change before that check. Values are provisional, not new rewards.</div>
      {story.objectiveTotals&&<div class="row-label" style="margin-top:6px">Completed: {story.objectiveTotals.completed} · Expired: {story.objectiveTotals.expired}</div>}
      {(story.objectives??[]).length===0&&<p style="font-size:13px">No active challenges. Offers appear at the next daily check.</p>}
      {(story.objectives??[]).map(o=><div key={o.id} class="card" style="padding:10px 12px;margin-top:8px"><div class="row-value">{o.name}</div><div class="row-label" style="white-space:normal">{o.kind==='weekly'?'Weekly':'Daily'} · {Math.floor(o.hoursLeft/24)}d {o.hoursLeft%24}h left</div>{o.requirement?<RequirementRow row={o.requirement}/>:<div class="row-label">Requirement unavailable. No progress inferred.</div>}<div class="row-label" style="white-space:normal;margin-top:6px">On completion: {o.rewardCashCents?`$${fmtMoney(o.rewardCashCents)}`:'No cash reward'}{o.reputation?`; ${o.reputation.track.replace(/_/g,' ')} reputation ${o.reputation.delta>=0?'+':''}${o.reputation.delta}`:''}.</div></div>)}

      <div class="eyebrow" style="margin-top:10px">Campaign - {wonCount} of {campaign.reserves.length} reserves won</div>
      {campaign.complete && (
        <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
          <div class="row-value">Campaign complete - all reserves won.</div>
        </div>
      )}
      {campaign.reserves.map((r, i) => (
        <div key={r.id} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
          <div style="display:flex;align-items:baseline;gap:8px">
            <span class="row-label">{i + 1}.</span>
            <div style="flex:1">
              <div class="row-value">{r.name}</div>
              <div class="row-label">{STATE_LABEL[r.state]}</div>
            </div>
          </div>
          {r.state === 'available' && (
            pendingBegin === r.id ? (
              <div style="margin-top:8px">
                <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em">
                  Starts a fresh ranch as {r.name}; your current ranch ends.
                </div>
                <div style="display:flex;gap:8px;margin-top:6px">
                  <button
                    class="btn btn-primary"
                    style="flex:1"
                    onClick={() => { setPendingBegin(null); bridge.send('story.begin', { reserve: r.id }); }}
                  >Confirm begin</button>
                  <button class="btn" style="flex:1" onClick={() => { setPendingBegin(null); }}>Not now</button>
                </div>
              </div>
            ) : (
              <button class="btn btn-primary" style="width:100%;margin-top:8px" onClick={() => { setPendingBegin(r.id); }}>
                Begin {r.name}
              </button>
            )
          )}
        </div>
      ))}

      <div class="eyebrow" style="margin-top:14px">Celebrated moments</div>
      {moments.map((m) => (
        <div key={m.id} style="display:flex;align-items:center;gap:8px;margin-top:6px">
          <span
            aria-hidden="true"
            style={`width:10px;height:10px;border-radius:5px;flex:none;background:${m.firedTick !== null ? 'var(--ui-amber-deep)' : 'var(--ui-ink-faint)'}`}
          />
          <span class="stealth-label" style={`flex:1;white-space:normal;${m.firedTick === null ? 'opacity:0.6' : ''}`}>{m.label}</span>
          {m.firedTick !== null && <span class="row-label">Day {Math.floor(m.firedTick / 24)}</span>}
        </div>
      ))}
    </div>
  );
}
